import authStore from ".";
import { authentication } from "@microsoft/teams-js";
import { graphService, graphClient, GraphService } from "@/services/graph-service";
import { saveRefreshToken, getRefreshToken, clearRefreshToken } from "@/utils/cookieUtils";
import { AuthenticationFunctions } from "@/commons/AuthenticationFunctions";
import calendarStore from "../calendar";
import userStore from "../users";
import notificationStore from "../notification";

const GRAPH_ACCESS_TOKEN_KEY = "graph_access_token";
const GRAPH_ACCESS_TOKEN_EXPIRES_KEY = "graph_access_token_expires";
const APP_ACCESS_TOKEN_KEY = "app_access_token";
const APP_ACCESS_TOKEN_EXPIRES_KEY = "app_access_token_expires";

function expiresInToDate(expiresIn: number | null): Date | null {
  if (!expiresIn) return null;
  return new Date(Date.now() + expiresIn * 1000);
}

async function getSsoToken(): Promise<string> {
  const token = await authentication.getAuthToken();
  return token;
}

export const TeamsAppAuth = {
  handleTeamsAuth: async () => {
    try {
      const { user } = authStore.getState();
      if (user?.isInTeams && user.teamsToken) {
        return;
      }

      const storedGraphToken = localStorage.getItem(GRAPH_ACCESS_TOKEN_KEY);
      const storedGraphExpires = localStorage.getItem(GRAPH_ACCESS_TOKEN_EXPIRES_KEY);
      const storedAppToken = localStorage.getItem(APP_ACCESS_TOKEN_KEY);
      const storedAppExpires = localStorage.getItem(APP_ACCESS_TOKEN_EXPIRES_KEY);

      const now = new Date();
      const fiveMinBuffer = new Date(now.getTime() + 5 * 60 * 1000);

      const graphTokenValid =
        storedGraphToken && storedGraphExpires && new Date(storedGraphExpires) > fiveMinBuffer;

      const appTokenValid =
        storedAppToken && storedAppExpires && new Date(storedAppExpires) > fiveMinBuffer;

      // Tokens expired or missing, acquire new ones
      const ssoToken = await getSsoToken();

      if (graphTokenValid && appTokenValid) {
        const storedAppTokenDecode: string | null = await AuthenticationFunctions.getTokenDataByKey(
          "preferred_username",
          storedAppToken
        );

        const ssoTokenDecode: string | null = await AuthenticationFunctions.getTokenDataByKey(
          "preferred_username",
          ssoToken
        );

        if (
          storedAppTokenDecode &&
          ssoTokenDecode &&
          storedAppTokenDecode?.trim()?.toLowerCase() === ssoTokenDecode.trim()?.toLowerCase()
        ) {
          await graphService.initializeWithToken(storedGraphToken, new Date(storedGraphExpires!));
          graphClient.setRefreshCallback(TeamsAppAuth.refreshGraphToken);

          authStore.setState({
            user: {
              ...user,
              isInTeams: true,
              teamsToken: storedAppToken
            }
          });

          await authStore.getState().fetchUserData();
          return;
        } else {
          localStorage.removeItem("lastLoggedInUser");
          calendarStore.getState().clearDefaultSelectedDateFromSessionStorage();
          calendarStore.getState().clearTimelineTimeRangeFromSessionStorage();
          userStore.getState().clearDefaultSelectedGroupsFromSessionStorage();
          userStore.getState().resetPageSize();
          notificationStore.getState().clearNotifications();
        }
      }

      graphClient.setRefreshCallback(TeamsAppAuth.refreshGraphToken);

      const { graphToken, appToken } = await acquireGraphToken(ssoToken);

      // Store graph token
      localStorage.setItem(GRAPH_ACCESS_TOKEN_KEY, graphToken.accessToken);
      const graphExpiresOn = expiresInToDate(graphToken.expiresIn);
      if (graphExpiresOn) {
        localStorage.setItem(GRAPH_ACCESS_TOKEN_EXPIRES_KEY, graphExpiresOn.toISOString());
      }
      if (graphToken.refreshToken) {
        saveRefreshToken(graphToken.refreshToken);
      }

      // Store app token
      localStorage.setItem(APP_ACCESS_TOKEN_KEY, appToken.accessToken);
      const appExpiresOn = expiresInToDate(appToken.expiresIn);
      if (appExpiresOn) {
        localStorage.setItem(APP_ACCESS_TOKEN_EXPIRES_KEY, appExpiresOn.toISOString());
      }

      await graphService.initializeWithToken(graphToken.accessToken, graphExpiresOn);

      authStore.setState({
        user: {
          ...user,
          isInTeams: true,
          teamsToken: appToken.accessToken
        }
      });

      await authStore.getState().fetchUserData();

      return;
    } catch (error) {
      console.error("Teams/Outlook authentication error:", error);
      throw error;
    }
  },

  refreshGraphToken: async (): Promise<{ accessToken: string; expiresOn: Date | null }> => {
    try {
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        try {
          const graphService = new GraphService();
          const refreshResult = await graphService.refreshOAuthToken(refreshToken, ".default");

          if (refreshResult.accessToken) {
            localStorage.setItem(GRAPH_ACCESS_TOKEN_KEY, refreshResult.accessToken);
            const expiresOn = expiresInToDate(refreshResult.expiresIn);
            if (expiresOn) {
              localStorage.setItem(GRAPH_ACCESS_TOKEN_EXPIRES_KEY, expiresOn.toISOString());
            }
            if (refreshResult.refreshToken) {
              saveRefreshToken(refreshResult.refreshToken);
            }
            return { accessToken: refreshResult.accessToken, expiresOn };
          }
        } catch (refreshError) {
          console.warn("Failed to refresh using cookie, falling back to SSO:", refreshError);
          clearRefreshToken();
        }
      }

      // Fallback: get new token via SSO + OBO (works for both Teams and Outlook)
      const ssoToken = await getSsoToken();
      const graphService = new GraphService();
      const graphTokenResponse = await graphService.getOAuthToken(ssoToken, ".default");

      localStorage.setItem(GRAPH_ACCESS_TOKEN_KEY, graphTokenResponse.accessToken);
      const expiresOn = expiresInToDate(graphTokenResponse.expiresIn);
      if (expiresOn) {
        localStorage.setItem(GRAPH_ACCESS_TOKEN_EXPIRES_KEY, expiresOn.toISOString());
      }
      if (graphTokenResponse.refreshToken) {
        saveRefreshToken(graphTokenResponse.refreshToken);
      }

      return { accessToken: graphTokenResponse.accessToken, expiresOn };
    } catch (error) {
      console.error("Failed to refresh graph token:", error);
      throw error;
    }
  },

  refreshAppToken: async (): Promise<string> => {
    try {
      const ssoToken = await getSsoToken();
      const graphService = new GraphService();
      const newAppToken = await graphService.getOAuthToken(
        ssoToken,
        import.meta.env.VITE_SCOPE_GRAPH_USER_READ_TEAM
      );

      // Update localStorage
      localStorage.setItem(APP_ACCESS_TOKEN_KEY, newAppToken.accessToken);
      const expiresOn = expiresInToDate(newAppToken.expiresIn);
      if (expiresOn) {
        localStorage.setItem(APP_ACCESS_TOKEN_EXPIRES_KEY, expiresOn.toISOString());
      }

      // Update auth store
      const { user } = authStore.getState();
      authStore.setState({
        user: { ...user, teamsToken: newAppToken.accessToken }
      });

      return newAppToken.accessToken;
    } catch (error) {
      console.error("Failed to refresh app token:", error);
      throw error;
    }
  }
};

async function acquireGraphToken(ssoToken: string): Promise<AcquireGraphTokenResponse> {
  try {
    const graphService = new GraphService();
    const appTokenResponse = graphService.getOAuthToken(
      ssoToken,
      import.meta.env.VITE_SCOPE_GRAPH_USER_READ_TEAM
    );

    const graphTokenResponse = graphService.getOAuthToken(ssoToken, ".default");

    const [appTokenResult, graphTokenResult] = await Promise.all([
      appTokenResponse,
      graphTokenResponse
    ]);

    if (!appTokenResult.accessToken) {
      throw new Error("No access token returned from backend");
    }

    return {
      appToken: {
        accessToken: appTokenResult.accessToken,
        expiresIn: appTokenResult.expiresIn
      },
      graphToken: {
        accessToken: graphTokenResult.accessToken,
        expiresIn: graphTokenResult.expiresIn,
        refreshToken: graphTokenResult.refreshToken
      }
    };
  } catch (error) {
    console.error("Failed to acquire graph token from backend:", error);
    throw error;
  }
}

type AcquireGraphTokenResponse = {
  graphToken: {
    accessToken: string;
    expiresIn: number | null;
    refreshToken: string | null;
  };
  appToken: {
    accessToken: string;
    expiresIn: number | null;
  };
};
